/**
 * DevBrains — Blog Content
 * ════════════════════════
 * All training articles and news stored here as static data.
 * Admin edits via /dashboard/admin/content → saved to localStorage.
 * localStorage keys: "db_blog_articles", "db_blog_news"
 *
 * To make permanent: copy from localStorage → paste SEED arrays here → redeploy.
 */

export const CATEGORIES = [
  { id: 'onboarding',    label: 'Onboarding',        icon: '🚀', color: '#16a34a' },
  { id: 'best-practice', label: 'Best Practices',    icon: '⭐', color: '#2563eb' },
  { id: 'career',        label: 'Career Growth',     icon: '📈', color: '#0891b2' },
  { id: 'technical',     label: 'Technical Skills',  icon: '💻', color: '#7c3aed' },
  { id: 'interview',     label: 'Interview Prep',    icon: '🎤', color: '#d97706' },
  { id: 'tools',         label: 'Tools & Workflow',  icon: '🛠️', color: '#6b7280' },
  { id: 'certification', label: 'Certification Guide', icon: '🏆', color: '#dc2626' },
];

export const NEWS_TYPES = [
  { id: 'Feature',      label: 'Feature',      color: '#16a34a', bg: '#f0fdf4' },
  { id: 'Update',       label: 'Update',       color: '#2563eb', bg: '#eff6ff' },
  { id: 'Maintenance',  label: 'Maintenance',  color: '#d97706', bg: '#fffbeb' },
  { id: 'Event',        label: 'Event',        color: '#7c3aed', bg: '#f5f3ff' },
  { id: 'Announcement', label: 'Announcement', color: '#0891b2', bg: '#f0f9ff' },
];

export const SEED_ARTICLES = [
  {
    id: 1,
    slug: "prepare-for-first-session",
    title: "How to Prepare for Your First Mentoring Session",
    category: "onboarding",
    emoji: "🎯",
    excerpt: "Make the most of your first 60 minutes with your mentor. Learn how to set clear goals, prepare the right questions, and walk away with an actionable plan.",
    body: `<h2>Before the Session</h2><p>The most successful mentoring sessions start with preparation. Before you even open the video call, spend 15 minutes doing the following:</p><h3>1. Define Your Goal</h3><p>Write down the <strong>one thing</strong> you want to walk away with. Not five things — one. "I want to understand how to structure a React project for scale" is a great goal. "I want to learn everything about React" is not.</p><h3>2. Prepare 3–5 Specific Questions</h3><p>Vague questions get vague answers. Compare these:</p><ul><li>❌ "How do I get better at coding?"</li><li>✅ "I'm building a Node.js API that needs to handle 10,000 requests/second — should I use clustering or worker threads, and why?"</li></ul><h3>3. Share Your Context</h3><p>Send your mentor a brief message before the session: your background, what you're building, and what you're stuck on. This saves the first 10 minutes of "so tell me about yourself."</p><h2>During the Session</h2><p>Take notes in real-time. Don't rely on memory. After every key point, paraphrase it back to confirm you understood correctly.</p><h2>After the Session</h2><p>Within 24 hours, write a 5-bullet summary of what you learned and your next action. Share it with your mentor — it keeps you accountable.</p><blockquote>The mentees who grow fastest are not the most talented — they're the most prepared.</blockquote>`,
    readTimeMins: 4,
    publishedAt: "2026-02-15",
    published: true,
    featured: true,
  },
  {
    id: 2,
    slug: "code-review-best-practices",
    title: "How to Get Maximum Value from a Code Review Session",
    category: "best-practice",
    emoji: "🔍",
    excerpt: "A code review session is not just about fixing bugs — it's about learning architectural thinking. Here's how to approach it the right way.",
    body: `<h2>What to Bring to a Code Review</h2><p>Don't just paste your code and say "what do you think?" Come with context about the problem, your constraints, what you're already unsure about, and what specific feedback you want.</p><h2>The Right Mindset</h2><p>Your code is not you. When your mentor says "this function is doing too much," they're showing you how senior engineers think about separation of concerns. Detach your ego from your code entirely.</p><h3>Questions to Ask During the Review</h3><ul><li>"How would you have approached this differently?"</li><li>"What's the production risk here?"</li><li>"Is this a style preference or a real problem?"</li><li>"What should I read to understand this pattern better?"</li></ul><h2>After the Review</h2><p>Implement every suggestion, then send the updated code to your mentor with a comment on each change explaining why you made it.</p>`,
    readTimeMins: 5,
    publishedAt: "2026-02-20",
    published: true,
    featured: false,
  },
  {
    id: 3,
    slug: "mock-interview-guide",
    title: "The Complete Guide to Mock Technical Interviews",
    category: "interview",
    emoji: "🎤",
    excerpt: "Mock interviews are only useful if you treat them like the real thing. Here's exactly how to run your mock interview for maximum impact.",
    body: `<h2>Set the Stage</h2><p>Treat every mock interview like it's the real thing: no notes, no second monitor, start a timer, dress professionally, and join 5 minutes early.</p><h2>How to Handle a Problem You Don't Know</h2><p>The biggest mistake candidates make is going silent. Silence is interpreted as incompetence. Instead, narrate your thinking out loud: "I haven't seen this exact problem before. Let me think about what data structure would help here..."</p><h2>The STAR Framework for Behavioral Questions</h2><p><strong>S</strong>ituation — <strong>T</strong>ask — <strong>A</strong>ction — <strong>R</strong>esult. Every behavioural answer should follow this. Keep it to 90 seconds maximum.</p><h2>After the Mock Interview</h2><p>Ask your mentor for a blunt score out of 10 across: problem-solving approach, code quality, communication, and confidence.</p>`,
    readTimeMins: 6,
    publishedAt: "2026-02-25",
    published: true,
    featured: false,
  },
  {
    id: 4,
    slug: "building-your-dev-portfolio",
    title: "Building a Portfolio That Gets You Hired",
    category: "career",
    emoji: "💼",
    excerpt: "Most developer portfolios are ignored by recruiters in under 10 seconds. Here's how to build one that gets a callback.",
    body: `<h2>The 3-Project Rule</h2><p>You don't need 15 projects. You need 3 <strong>great</strong> ones. Recruiters don't scroll — they skim. Pick your three strongest projects and make them shine.</p><h2>What Makes a Project "Great"?</h2><ul><li><strong>It solves a real problem</strong> — not a tutorial clone</li><li><strong>It has a live demo</strong> — recruiters won't run your code locally</li><li><strong>It shows depth</strong> — authentication, database, error handling, tests</li><li><strong>The README explains WHY, not just what</strong></li></ul><h2>The Project Description Formula</h2><p>"[Name] is a [what it is] that helps [who] to [do what], built with [stack]. Key challenges solved: [1-2 interesting technical problems]."</p><h2>GitHub Profile Matters</h2><p>Pin your 6 best repos. Write a profile README. Keep your contribution graph green. Recruiters at top companies do look at GitHub.</p>`,
    readTimeMins: 5,
    publishedAt: "2026-03-01",
    published: true,
    featured: true,
  },
  {
    id: 5,
    slug: "certification-program-guide",
    title: "How the DevBrains Certification Program Works",
    category: "certification",
    emoji: "🏆",
    excerpt: "A step-by-step walkthrough of the DevBrains project-based certification — from enrollment to receiving your verified certificate.",
    body: `<h2>What Makes DevBrains Certificates Different</h2><p>Unlike course completion certificates, a DevBrains certificate means you built something real, had it reviewed line by line by a senior engineer, and defended it in a live technical interview.</p><h2>The 4 Stages</h2><h3>Stage 1: Enrollment & Project Brief</h3><p>After enrollment, your assigned mentor sends you a detailed project brief within 48 hours including scope, technical requirements, and weekly milestones.</p><h3>Stage 2: Daily Submissions</h3><p>Submit your progress daily. Your mentor reviews each submission and leaves feedback within 24 hours.</p><h3>Stage 3: Final Code Review</h3><p>A comprehensive review of your entire codebase — architecture, code quality, security, and performance.</p><h3>Stage 4: Technical Interview</h3><p>A 30-minute 1:1 session where your mentor asks questions about your design decisions and verifies you can explain every line you wrote.</p>`,
    readTimeMins: 5,
    publishedAt: "2026-03-05",
    published: true,
    featured: false,
  },
];

export const SEED_NEWS = [
  {
    id: 1,
    slug: "teams-support-launch",
    title: "DevBrains Now Supports Microsoft Teams for Sessions",
    newsType: "Feature",
    emoji: "🪟",
    summary: "Mentors can now set Microsoft Teams as their preferred meeting platform alongside Zoom and Google Meet.",
    body: `<p>We're excited to announce that <strong>Microsoft Teams</strong> is now a supported meeting platform on DevBrains. Mentors who work in enterprise environments can now set it as their default platform.</p><p>To update your meeting platform, go to <strong>Dashboard → My Profile → Meeting Settings</strong>.</p>`,
    date: "2026-03-06",
    isPinned: true,
    published: true,
  },
  {
    id: 2,
    slug: "two-factor-auth-launch",
    title: "New Feature: Two-Factor Authentication Now Available",
    newsType: "Feature",
    emoji: "🔐",
    summary: "Secure your DevBrains account with TOTP-based two-factor authentication. Enable it from your Security settings.",
    body: `<p>Account security is a top priority at DevBrains. We've launched <strong>Two-Factor Authentication (2FA)</strong> using TOTP — compatible with Google Authenticator, Authy, and Microsoft Authenticator.</p><p>To enable 2FA, go to <strong>Dashboard → Security → Two-Factor Authentication</strong>.</p>`,
    date: "2026-03-05",
    isPinned: false,
    published: true,
  },
  {
    id: 3,
    slug: "maintenance-march-10",
    title: "Scheduled Maintenance — March 10, 2026",
    newsType: "Maintenance",
    emoji: "🔧",
    summary: "DevBrains will undergo scheduled maintenance on March 10 from 2:00 AM to 4:00 AM IST. Booking and payments will be unavailable during this window.",
    body: `<p>We have scheduled maintenance on <strong>March 10, 2026 from 2:00 AM to 4:00 AM IST</strong>.</p><p>During this window the platform will be in read-only mode — new bookings and payments will be unavailable, but existing session links will continue to work.</p>`,
    date: "2026-03-04",
    isPinned: false,
    published: true,
  },
  {
    id: 4,
    slug: "aiml-certification-launch",
    title: "Introducing Project-Based Certifications for AI/ML",
    newsType: "Feature",
    emoji: "🤖",
    summary: "DevBrains now offers an AI/ML certification track — build a real ML project, get it reviewed, and earn a verified certificate.",
    body: `<p>We're thrilled to launch our <strong>AI/ML Certification Track</strong>. The 8-week program covers data preprocessing, model training, evaluation, and deployment. Price: ₹3,999.</p>`,
    date: "2026-02-28",
    isPinned: false,
    published: true,
  },
  {
    id: 5,
    slug: "platform-launch",
    title: "Welcome to DevBrains — We're Live!",
    newsType: "Announcement",
    emoji: "🚀",
    summary: "DevBrains is officially live. Connect with verified mentors from top tech companies for 1:1 sessions, code reviews, and mock interviews.",
    body: `<p>We built DevBrains because the fastest way to grow as a developer is to learn from someone who's already where you want to be. Your first session is free. <a href="/mentors">Find your mentor →</a></p>`,
    date: "2026-01-15",
    isPinned: false,
    published: true,
  },
];

// ─────────────────────────────────────────────
// HELP & FAQ ARTICLES  (For Mentees)
// ─────────────────────────────────────────────

export const HELP_ARTICLES = [
  {
    id: 101,
    slug: "how-to-find-a-mentor",
    title: "How to Find the Right Mentor on DevBrains",
    category: "onboarding",
    emoji: "🔍",
    excerpt: "Not sure where to start? Follow this step-by-step guide to find the perfect mentor for your goals, tech stack, and budget.",
    body: `<h2>Step 1 — Know What You Need</h2>
<p>Before searching, write down three things: the skill you want to improve, your current level (beginner/intermediate/advanced), and your goal (job change, project help, interview prep). A clear goal gets you a better mentor match.</p>
<h2>Step 2 — Use the Search & Filters</h2>
<p>Go to <strong>Find Mentors</strong> and use the filters:</p>
<ul>
  <li><strong>Tech Stack</strong> — React, Node.js, Python, AWS, etc.</li>
  <li><strong>Price</strong> — from ₹500/hr to ₹5,000/hr</li>
  <li><strong>Rating</strong> — filter by 4★ and above</li>
  <li><strong>Availability</strong> — today, this week, weekends</li>
  <li><strong>Language</strong> — English, Hindi, Tamil, etc.</li>
</ul>
<h2>Step 3 — Read the Profile Carefully</h2>
<p>A mentor's profile tells you everything: where they work, how many sessions they've done, what mentees say about them, and exactly what they can help with. Pay special attention to the <strong>specialisations</strong> and <strong>testimonials</strong> sections — these are written by real mentees who worked with them.</p>
<h2>Step 4 — Book a Free Introductory Session</h2>
<p>Every new mentee on DevBrains gets <strong>one free 30-minute session</strong>. Use it wisely — come prepared with your goals, a specific question, and a brief intro of your background. This is your chance to see if the mentor's style matches how you learn.</p>
<h2>Step 5 — Check Availability Before Booking</h2>
<p>On the mentor's profile, click <strong>View Availability</strong> to see their open slots. You can book up to 7 days in advance. After booking you'll receive a confirmation email with the meeting link.</p>
<blockquote>Pro tip: Mentors with "Quick Responder" badges reply to messages within 2 hours on average.</blockquote>`,
    readTimeMins: 4,
    publishedAt: "2026-03-06",
    published: true,
    featured: true,
  },
  {
    id: 102,
    slug: "how-payment-works",
    title: "How Payment Works on DevBrains",
    category: "onboarding",
    emoji: "💳",
    excerpt: "Everything you need to know about how DevBrains handles payments — from booking to refunds, all secured by Razorpay.",
    body: `<h2>Secure Payment via Razorpay</h2>
<p>All payments on DevBrains are processed by <strong>Razorpay</strong>, India's most trusted payment gateway. We never store your card details — they're handled entirely by Razorpay's PCI-DSS compliant infrastructure.</p>
<h2>How the Payment Flow Works</h2>
<ol>
  <li>You find a mentor and click <strong>Book Session</strong></li>
  <li>Select a date and time slot</li>
  <li>A Razorpay checkout opens — pay via UPI, card, net banking, or wallet</li>
  <li>Payment is held in <strong>escrow</strong> — not released to the mentor yet</li>
  <li>Session happens</li>
  <li>You mark the session as completed (or it auto-completes after 24 hours)</li>
  <li>Payment releases to the mentor's wallet, minus DevBrains' 15% platform fee</li>
</ol>
<h2>Accepted Payment Methods</h2>
<ul>
  <li>✅ UPI (GPay, PhonePe, Paytm, BHIM)</li>
  <li>✅ Credit / Debit Cards (Visa, Mastercard, RuPay)</li>
  <li>✅ Net Banking (all major banks)</li>
  <li>✅ Wallets (Paytm, Mobikwik, Freecharge)</li>
  <li>✅ EMI (for sessions above ₹3,000)</li>
</ul>
<h2>Invoice & Receipt</h2>
<p>After every successful payment, you'll receive a GST-compliant invoice by email and it'll also appear in your <strong>Dashboard → Billing</strong> section for download anytime.</p>
<h2>Currency</h2>
<p>DevBrains displays prices in INR by default. You can switch to USD, SGD, or MYR using the currency selector in the top navigation — but all transactions process in INR.</p>`,
    readTimeMins: 4,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 103,
    slug: "mentor-did-not-join",
    title: "What to Do If Your Mentor Doesn't Join the Session",
    category: "best-practice",
    emoji: "⚠️",
    excerpt: "If your mentor misses the session, here is exactly what happens next — and how DevBrains protects you with an automatic refund policy.",
    body: `<h2>What Happens Automatically</h2>
<p>If your mentor does not join within <strong>15 minutes</strong> of the scheduled start time, DevBrains' system automatically marks the session as a <strong>mentor no-show</strong>. You do not need to do anything manually.</p>
<h2>Your Automatic Protection</h2>
<ul>
  <li>✅ <strong>Full refund</strong> — 100% of the session amount is returned to your original payment method within 5–7 business days</li>
  <li>✅ <strong>Free re-booking credit</strong> — you get a credit to book one session with any mentor at no extra charge</li>
  <li>✅ <strong>Mentor penalty</strong> — the mentor's no-show is recorded; 3 no-shows results in account suspension</li>
</ul>
<h2>What You Should Do</h2>
<ol>
  <li>Wait 15 minutes after the scheduled time</li>
  <li>Check your email — you'll get an automatic "Session Missed" notification</li>
  <li>Go to <strong>Dashboard → My Sessions</strong> and click <strong>Report No-Show</strong></li>
  <li>Refund will be initiated automatically</li>
</ol>
<h2>Need Help Faster?</h2>
<p>If the session auto-close hasn't triggered within 30 minutes, use the <strong>Live Chat</strong> button or email <strong>support@devbrains.in</strong> and we'll resolve it within 2 hours.</p>
<blockquote>DevBrains has a 98.7% mentor show-up rate across 12,000+ sessions. No-shows are rare, but when they happen, you're fully protected.</blockquote>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 104,
    slug: "cancellation-policy",
    title: "DevBrains Cancellation & Refund Policy Explained",
    category: "onboarding",
    emoji: "🔄",
    excerpt: "Need to cancel a session? Here's exactly how our cancellation policy works, when you get a full refund, and how to reschedule.",
    body: `<h2>Cancellation Window & Refund Rules</h2>
<table>
  <tr><th>When You Cancel</th><th>Refund Amount</th></tr>
  <tr><td>More than 24 hours before session</td><td>✅ 100% full refund</td></tr>
  <tr><td>2–24 hours before session</td><td>⚠️ 50% refund</td></tr>
  <tr><td>Less than 2 hours before session</td><td>❌ No refund</td></tr>
  <tr><td>Mentor no-show</td><td>✅ 100% full refund + free rebooking credit</td></tr>
</table>
<h2>How to Cancel a Session</h2>
<ol>
  <li>Go to <strong>Dashboard → My Sessions</strong></li>
  <li>Find the upcoming session and click <strong>Cancel</strong></li>
  <li>Select a reason (optional but helps us improve)</li>
  <li>Confirm — you'll get an email with the refund timeline</li>
</ol>
<h2>Reschedule Instead of Cancel</h2>
<p>You can reschedule a session <strong>up to 4 hours before</strong> the start time with no penalty. Go to the session and click <strong>Reschedule</strong> to pick a new slot. Rescheduling is always free.</p>
<h2>Refund Timeline</h2>
<ul>
  <li><strong>UPI / Wallets:</strong> 1–3 business days</li>
  <li><strong>Credit / Debit Cards:</strong> 5–7 business days</li>
  <li><strong>Net Banking:</strong> 3–5 business days</li>
</ul>
<p>Refunds go back to the original payment method. You'll receive an email confirmation when the refund is initiated.</p>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 105,
    slug: "mentor-missed-meeting",
    title: "What Happens If a Mentor Misses Your Scheduled Meeting?",
    category: "best-practice",
    emoji: "🚨",
    excerpt: "Your time and money are protected. Here's our complete no-show policy, automatic refunds, and what happens to the mentor.",
    body: `<h2>The 15-Minute Rule</h2>
<p>If your mentor hasn't joined the video call within <strong>15 minutes</strong> of the scheduled start, the session is automatically classified as a <strong>mentor no-show</strong>. You are NOT charged for this session.</p>
<h2>What Happens to Your Payment</h2>
<p>Your payment was held in escrow and <strong>was never released to the mentor</strong>. A full refund is automatically initiated to your original payment method. No action needed from you.</p>
<h2>What Happens to the Mentor</h2>
<ul>
  <li><strong>1st no-show:</strong> Warning issued, visible on their profile for 30 days</li>
  <li><strong>2nd no-show:</strong> Profile temporarily hidden from search results for 7 days</li>
  <li><strong>3rd no-show:</strong> Account suspended — DevBrains removes their ability to accept bookings</li>
</ul>
<h2>Emergency Exceptions</h2>
<p>If a mentor contacts you and DevBrains support <strong>before</strong> the session with a genuine emergency (medical, family crisis), they can request a one-time exception. In this case, the session is rescheduled and the mentor's record is not penalised. You still get to choose whether to reschedule or take a full refund.</p>
<h2>How to Report a No-Show</h2>
<p>If the 15-minute auto-detection hasn't triggered, go to <strong>Dashboard → My Sessions → Report No-Show</strong>. Our support team will review within 2 hours and process your refund manually if needed.</p>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 106,
    slug: "why-devbrains-is-trusted",
    title: "Why DevBrains is One of the Most Trusted Mentorship Platforms",
    category: "onboarding",
    emoji: "🛡️",
    excerpt: "From verified mentor profiles to escrow payments and blockchain certificates — here's exactly how DevBrains earns your trust.",
    body: `<h2>1. Every Mentor is Verified</h2>
<p>DevBrains manually verifies every mentor before they can accept bookings. Verification includes:</p>
<ul>
  <li>✅ LinkedIn profile verification — we confirm they work where they say they do</li>
  <li>✅ ID verification via DigiLocker / Aadhaar (India) or passport (international)</li>
  <li>✅ Technical screening — an existing senior mentor interviews every applicant</li>
  <li>✅ Background check — no criminal record, no fraud history</li>
</ul>
<h2>2. Escrow Payment Protection</h2>
<p>Your payment is <strong>never released to the mentor until the session is completed</strong>. It's held in Razorpay's secure escrow. If anything goes wrong — no-show, technical failure, genuine dissatisfaction — your money is protected.</p>
<h2>3. Honest Reviews You Can Trust</h2>
<p>DevBrains only allows reviews from mentees who have <strong>completed a paid session</strong>. You cannot review a mentor unless you've booked and attended. This means every rating you see is real.</p>
<h2>4. Blockchain-Verified Certificates</h2>
<p>Our project-based certificates are stored on a public blockchain. Any employer can verify the certificate's authenticity using the unique certificate ID — no forgery possible.</p>
<h2>5. Transparent Pricing, No Hidden Fees</h2>
<p>The price you see on a mentor's profile is exactly what you pay. No booking fees, no surprise charges. The only additional cost is GST (18%) which is clearly shown at checkout.</p>
<h2>6. Data Privacy</h2>
<p>DevBrains is fully GDPR and PDPA compliant. We never sell your data, never share it with advertisers, and you can export or delete your data anytime from your profile settings.</p>
<blockquote>12,000+ sessions completed · ₹2.4 Cr paid to mentors · 4.9★ average platform rating</blockquote>`,
    readTimeMins: 5,
    publishedAt: "2026-03-06",
    published: true,
    featured: true,
  },
  {
    id: 107,
    slug: "how-mentors-get-ratings",
    title: "How Mentors Get Ratings and Testimonials on DevBrains",
    category: "best-practice",
    emoji: "⭐",
    excerpt: "Ratings and testimonials are earned, not bought. Here's exactly how the review system works — for both mentees and mentors.",
    body: `<h2>When Can a Mentee Leave a Review?</h2>
<p>A mentee can only leave a rating and testimonial <strong>after a session is marked as completed</strong>. Reviews are locked to verified sessions — you cannot review a mentor you haven't had a paid session with. This keeps all reviews genuine.</p>
<h2>The Review Process After a Session</h2>
<ol>
  <li>Session ends and is marked complete (auto or manually)</li>
  <li>Within 1 hour, the mentee receives an email: <strong>"How was your session with [Mentor]?"</strong></li>
  <li>Mentee rates on 5 dimensions: Communication, Knowledge, Punctuality, Helpfulness, Overall</li>
  <li>Mentee writes a short testimonial (optional but encouraged)</li>
  <li>Mentee selects tags: e.g. "Great code reviewer", "Patient teacher", "Industry insider"</li>
  <li>Review is published on the mentor's profile within 24 hours after moderation</li>
</ol>
<h2>For Group Sessions</h2>
<p>After a group session or webinar completes, <strong>every attendee</strong> who attended at least 75% of the session receives the review prompt. All attendees can leave independent ratings — the mentor's score updates with the average of all new ratings.</p>
<h2>As a Mentor — How to Earn Better Reviews</h2>
<ul>
  <li>Start and end sessions on time — punctuality is a rated category</li>
  <li>Send a brief prep message to mentees before the session</li>
  <li>End every session with a clear next-action summary in the chat</li>
  <li>Follow up with resources mentioned in the session (links, articles)</li>
  <li>Ask the mentee what went well and what could be better — this shows you care</li>
</ul>
<h2>Review Moderation</h2>
<p>All reviews go through a 24-hour moderation check. DevBrains removes reviews that contain hate speech, false claims, or personal attacks. Mentors can flag a review for investigation if they believe it's fraudulent.</p>`,
    readTimeMins: 4,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 108,
    slug: "how-to-become-a-mentor",
    title: "How to Become a Mentor on DevBrains",
    category: "certification",
    emoji: "🧑‍🏫",
    excerpt: "Think you have what it takes to mentor the next generation of engineers? Here's the complete step-by-step guide to applying and getting approved.",
    body: `<h2>Who Can Become a Mentor?</h2>
<p>DevBrains accepts mentors who have <strong>at least 3 years of professional experience</strong> in their technical domain. We specifically look for engineers who are currently working at technology companies (startups, mid-size, or FAANG-tier) — not just instructors or teachers.</p>
<h2>Step 1 — Submit Your Application</h2>
<p>Click <strong>Apply Now</strong> on the Become a Mentor page. Fill in:</p>
<ul>
  <li>Your professional background and current role</li>
  <li>LinkedIn profile URL (mandatory)</li>
  <li>Areas you can mentor in (your tech stack)</li>
  <li>Your proposed hourly rate</li>
  <li>A brief "mentor statement" — why do you want to mentor?</li>
</ul>
<h2>Step 2 — Technical Screening</h2>
<p>Within 3–5 business days, our team reviews your application. If shortlisted, you'll be invited to a <strong>30-minute technical interview</strong> with a senior DevBrains mentor. This is conversational — we're checking your communication and teaching ability, not your algorithm skills.</p>
<h2>Step 3 — Profile Setup</h2>
<p>Once approved, you'll receive an email with a setup guide. You'll:</p>
<ul>
  <li>Complete your profile (skills, experience, photo, bio)</li>
  <li>Set your availability slots</li>
  <li>Choose your meeting platform (Zoom, Google Meet, or Teams)</li>
  <li>Set up bank account details for payouts</li>
  <li>Complete the Mentor Handbook review</li>
</ul>
<h2>Step 4 — Go Live</h2>
<p>After your profile is reviewed and approved (usually within 24 hours of submission), you'll appear in search results and can start accepting bookings.</p>
<blockquote>Top mentors on DevBrains earn ₹50,000–₹1,50,000 per month. Your first booking usually comes within the first week if your profile is complete.</blockquote>`,
    readTimeMins: 5,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
  {
    id: 109,
    slug: "mentor-approval-process",
    title: "The Mentor Approval Process — What Happens After You Apply",
    category: "certification",
    emoji: "✅",
    excerpt: "Curious about what DevBrains checks during mentor verification? Here's a transparent look at our full approval process.",
    body: `<h2>Stage 1: Application Review (1–2 days)</h2>
<p>Our team reviews your application form. We check:</p>
<ul>
  <li>Professional experience — minimum 3 years in your field</li>
  <li>LinkedIn consistency — your application matches your LinkedIn</li>
  <li>Technical depth — your skills match your stated experience level</li>
  <li>Communication quality — your mentor statement shows you can communicate clearly</li>
</ul>
<p>If your application passes this stage, you'll receive a calendar invite for a screening call.</p>
<h2>Stage 2: Technical Screening Interview (30 min)</h2>
<p>A senior DevBrains mentor conducts a 30-minute video call. This is NOT a technical test — we're assessing:</p>
<ul>
  <li>Can you explain complex topics simply?</li>
  <li>Are you genuinely interested in helping others grow?</li>
  <li>Do you have real-world project experience (not just theoretical)?</li>
  <li>Is your English or regional language communication clear and professional?</li>
</ul>
<h2>Stage 3: Identity & Background Verification (1–3 days)</h2>
<p>Accepted candidates complete identity verification via our KYC partner. This is required by RBI regulations for mentor payouts and takes about 5 minutes.</p>
<h2>Stage 4: Profile Review (24 hours)</h2>
<p>Our team reviews your completed mentor profile — photo, bio, skills, pricing — to ensure it meets quality standards. We may suggest improvements before publishing.</p>
<h2>Timeline</h2>
<p>End-to-end, the approval process takes <strong>5–10 business days</strong>. You'll receive status emails at every stage. If rejected at any stage, you'll receive a detailed reason and can reapply after 90 days.</p>
<h2>Approval Rate</h2>
<p>DevBrains approves approximately <strong>1 in 5 applications</strong>. We keep standards high because our mentees trust that every approved mentor is genuinely excellent.</p>`,
    readTimeMins: 4,
    publishedAt: "2026-03-06",
    published: true,
    featured: false,
  },
];

// ─────────────────────────────────────────────
// PLATFORM GUIDE ARTICLES  (Footer link targets)
// ─────────────────────────────────────────────

export const GUIDE_ARTICLES = [
  {
    id: 201,
    slug: "find-a-mentor-guide",
    title: "Find a Mentor — Complete Guide",
    emoji: "🔍",
    excerpt: "Search, filter, compare and book the right mentor for your goals in minutes.",
    body: `<h2>The DevBrains Mentor Network</h2>
<p>DevBrains has 2,400+ verified mentors across 50+ technology domains — from React and Node.js to Machine Learning, iOS, DevOps, and System Design. Every mentor is manually vetted, professionally employed, and actively taking bookings.</p>
<h2>How to Search</h2>
<p>Use the filters on the <a href="/mentors">Find Mentors</a> page to narrow down by skill, price, rating, availability, and language. You can also search by company (e.g., "Google", "Shopee") to find mentors with experience at specific organisations.</p>
<h2>Understanding Mentor Tiers</h2>
<ul>
  <li>🟢 <strong>New Mentor</strong> — recently verified, great for budget sessions</li>
  <li>🔵 <strong>Verified</strong> — 10+ sessions completed, consistent ratings</li>
  <li>🟣 <strong>Top Mentor</strong> — 50+ sessions, 4.8★+ rating, featured placement</li>
  <li>⭐ <strong>Elite</strong> — invite-only, 100+ sessions, FAANG or equivalent background</li>
</ul>
<h2>Your First Session is Free</h2>
<p>Every new mentee on DevBrains gets one free 30-minute introductory session with any mentor. No credit card needed. Use it to test the mentor fit before committing to a paid session.</p>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
  },
  {
    id: 202,
    slug: "get-certified-guide",
    title: "Get Certified — Project-Based Certifications",
    emoji: "🏆",
    excerpt: "Earn certificates that employers actually trust — built on real projects, reviewed by engineers, defended in live interviews.",
    body: `<h2>What Makes DevBrains Certificates Different</h2>
<p>Most online certificates test whether you watched a video. DevBrains certificates test whether you can <strong>build, explain, and defend</strong> a real project under expert review. That's why companies trust them.</p>
<h2>The Certification Process</h2>
<ol>
  <li><strong>Enroll</strong> — choose your track (Full Stack, AI/ML, Mobile, Cloud)</li>
  <li><strong>Build</strong> — complete a real project with daily submissions</li>
  <li><strong>Review</strong> — your mentor reviews every code submission</li>
  <li><strong>Defend</strong> — 30-minute live technical interview with your mentor</li>
  <li><strong>Certify</strong> — receive a blockchain-verified certificate</li>
</ol>
<h2>Available Tracks</h2>
<ul>
  <li>Full Stack Web Development (React + Node.js)</li>
  <li>AI/ML Engineering (Python + TensorFlow)</li>
  <li>Mobile Development (React Native / Flutter)</li>
  <li>Cloud & DevOps (AWS / GCP)</li>
  <li>System Design & Architecture</li>
</ul>
<h2>How Employers Verify</h2>
<p>Each certificate has a unique ID and a public verification URL. Employers paste it into our verification portal and see the full project summary, mentor's assessment, and your technical interview score.</p>`,
    readTimeMins: 4,
    publishedAt: "2026-03-06",
    published: true,
  },
  {
    id: 203,
    slug: "browse-by-tech",
    title: "Browse Mentors by Technology Stack",
    emoji: "💻",
    excerpt: "Looking for a React expert? A Python data scientist? A Kubernetes engineer? Find specialists by exact technology.",
    body: `<h2>Technology Categories</h2>
<p>DevBrains organises mentors by 50+ technology categories. Instead of searching broadly, you can go directly to the experts in your specific tech.</p>
<h2>Most In-Demand Technologies</h2>
<ul>
  <li>⚛️ <strong>React / React Native</strong> — 180+ mentors</li>
  <li>🟢 <strong>Node.js</strong> — 140+ mentors</li>
  <li>🐍 <strong>Python / Django / FastAPI</strong> — 200+ mentors</li>
  <li>☁️ <strong>AWS / GCP / Azure</strong> — 120+ mentors</li>
  <li>🤖 <strong>Machine Learning / Deep Learning</strong> — 90+ mentors</li>
  <li>📱 <strong>iOS / Android / Flutter</strong> — 75+ mentors</li>
  <li>🔧 <strong>DevOps / Kubernetes / Docker</strong> — 85+ mentors</li>
  <li>🏗️ <strong>System Design</strong> — 110+ mentors</li>
  <li>📊 <strong>Data Engineering</strong> — 60+ mentors</li>
  <li>🔐 <strong>Cybersecurity</strong> — 45+ mentors</li>
</ul>
<h2>How to Filter by Tech</h2>
<p>On the <a href="/mentors">Find Mentors</a> page, use the <strong>Skills</strong> filter. You can select multiple technologies — for example, filter by "React" AND "TypeScript" to find mentors who are strong in both.</p>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
  },
  {
    id: 204,
    slug: "group-sessions-guide",
    title: "Group Sessions — Learn Together, Save More",
    emoji: "👥",
    excerpt: "Join live group sessions with top mentors. Learn alongside other developers, ask questions, and pay a fraction of a 1:1 price.",
    body: `<h2>What Are Group Sessions?</h2>
<p>Group sessions are live online classes with 4–15 participants. A verified DevBrains mentor teaches, demonstrates, and answers questions in real-time. Think of it as a small, focused workshop — not a recorded video course.</p>
<h2>How They Work</h2>
<ol>
  <li>Browse upcoming sessions on the <a href="/events">Events</a> page</li>
  <li>Register (most sessions are ₹299–₹999)</li>
  <li>Receive the meeting link by email</li>
  <li>Attend live — ask questions, share your screen, interact</li>
  <li>Get the recording within 24 hours if you miss it</li>
</ol>
<h2>Topics Covered</h2>
<ul>
  <li>Weekly "Code Review Friday" — live code review sessions</li>
  <li>System Design Masterclass (bi-weekly)</li>
  <li>Interview Prep workshops (FAANG-focused)</li>
  <li>Tech-specific deep dives (React Performance, Docker basics, etc.)</li>
  <li>Career AMA sessions with senior engineers</li>
</ul>
<h2>Pricing</h2>
<p>Group sessions are priced at <strong>10–20% of a 1:1 session</strong> with the same mentor. They're the best way to learn from top mentors on a budget.</p>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
  },
  {
    id: 205,
    slug: "career-roadmap-guide",
    title: "Career Roadmap — Plan Your Path to Your Dream Job",
    emoji: "🗺️",
    excerpt: "Not sure what to learn next? Use the DevBrains Career Roadmap to plan your learning journey from where you are to where you want to be.",
    body: `<h2>What is the Career Roadmap?</h2>
<p>The Career Roadmap is a personalised learning path built with your mentor. Based on your current skills, target role, and timeline, your mentor helps you identify exactly what to learn, in what order, and what projects to build to get there.</p>
<h2>Popular Career Paths</h2>
<ul>
  <li>🎯 <strong>Junior → Senior Developer</strong> (12–18 months)</li>
  <li>🎯 <strong>Backend Developer → Full Stack</strong> (6–9 months)</li>
  <li>🎯 <strong>Developer → ML Engineer</strong> (9–12 months)</li>
  <li>🎯 <strong>Dev → Engineering Manager</strong> (18–24 months)</li>
  <li>🎯 <strong>Fresher → First Job</strong> (3–6 months)</li>
  <li>🎯 <strong>Developer → FAANG</strong> (6–18 months)</li>
</ul>
<h2>How to Start</h2>
<ol>
  <li>Book a <strong>Career Clarity Session</strong> with a mentor who has your target role</li>
  <li>Your mentor creates a personalised roadmap in your first session</li>
  <li>Follow up monthly to track progress and adjust the plan</li>
  <li>Build projects that prove your new skills</li>
  <li>Get a DevBrains certification when you're ready</li>
</ol>`,
    readTimeMins: 3,
    publishedAt: "2026-03-06",
    published: true,
  },
];

// ─────────────────────────────────────────────
// MENTOR GUIDE ARTICLES  (For Mentors)
// ─────────────────────────────────────────────

export const MENTOR_GUIDE_ARTICLES = [
  {
    id: 301,
    slug: "mentor-handbook",
    title: "The DevBrains Mentor Handbook",
    emoji: "📖",
    excerpt: "Everything you need to know to run successful mentoring sessions, build your reputation, and grow your income on DevBrains.",
    body: `<h2>Welcome to the DevBrains Mentor Community</h2>
<p>You're now part of a network of 2,400+ verified engineers helping developers around the world grow their skills and careers. This handbook covers everything you need to deliver excellent sessions and build a thriving mentorship practice.</p>
<h2>Setting Up for Success</h2>
<h3>Complete Your Profile</h3>
<p>A complete profile gets 3x more bookings than an incomplete one. Make sure you have:</p>
<ul>
  <li>✅ Professional photo (high quality, neutral background)</li>
  <li>✅ Compelling headline (e.g., "Senior ML Engineer @ Shopee | Helping developers break into AI")</li>
  <li>✅ Detailed bio with your specialisations and what kind of mentees you work best with</li>
  <li>✅ All relevant skills tagged</li>
  <li>✅ At least one session type listed with a clear description</li>
</ul>
<h3>Set Realistic Availability</h3>
<p>Only list slots you can genuinely commit to. Cancellations hurt your rating and DevBrains will penalise accounts with high cancellation rates.</p>
<h2>Running Great Sessions</h2>
<ul>
  <li>Send a prep message 24 hours before: "Looking forward to our session tomorrow. Could you share what you're working on?"</li>
  <li>Start with a clear agenda: "Here's what I'd like us to cover today..."</li>
  <li>End with a written summary in chat: key takeaways + next steps</li>
  <li>Keep sessions running on time — mentees rate punctuality separately</li>
</ul>
<h2>Growing Your Reputation</h2>
<p>Your first 5 reviews are the most important. Consider offering your first few sessions at a slightly lower rate to build up genuine testimonials quickly.</p>
<blockquote>Top DevBrains mentors earn ₹80,000–₹1,50,000 per month. The difference between a good mentor and a top mentor is mostly in the pre/post session communication, not the session itself.</blockquote>`,
    readTimeMins: 7,
    publishedAt: "2026-03-06",
    published: true,
  },
  {
    id: 302,
    slug: "mentor-payout-info",
    title: "Mentor Payout — How You Get Paid on DevBrains",
    emoji: "💰",
    excerpt: "Clear, transparent payout information for DevBrains mentors — how earnings are calculated, when they're released, and how to withdraw.",
    body: `<h2>How Earnings Are Calculated</h2>
<p>When a mentee books a paid session:</p>
<ul>
  <li>Mentee pays full amount at booking (held in escrow)</li>
  <li>After session completes, DevBrains releases the payment</li>
  <li>DevBrains deducts a <strong>15% platform commission</strong></li>
  <li>Remaining <strong>85% goes to your wallet</strong></li>
</ul>
<h3>Example</h3>
<p>Mentee pays ₹2,000 for a session → DevBrains fee: ₹300 (15%) → Your earnings: ₹1,700 (85%)</p>
<h2>Wallet & Withdrawal</h2>
<p>Your earnings accumulate in your DevBrains wallet. You can request a payout at any time as long as your wallet balance is above <strong>₹500</strong>.</p>
<h2>Payout Methods</h2>
<ul>
  <li>✅ Bank Transfer (NEFT/IMPS) — all Indian banks supported</li>
  <li>✅ UPI — instant, minimum ₹500</li>
  <li>✅ International Wire Transfer (SWIFT) — for non-Indian mentors, minimum $50 equivalent</li>
</ul>
<h2>Payout Timeline</h2>
<ul>
  <li><strong>UPI:</strong> Instant (within 2 hours of request)</li>
  <li><strong>Bank Transfer:</strong> 1–3 business days</li>
  <li><strong>International Wire:</strong> 3–7 business days</li>
</ul>
<h2>TDS Deduction</h2>
<p>DevBrains deducts <strong>TDS at 10%</strong> on platform earnings as required by Indian tax law (Section 194H) for annual earnings above ₹15,000. You'll receive a TDS certificate (Form 16A) at the end of every quarter, downloadable from your dashboard.</p>
<h2>GST for Mentors</h2>
<p>If your annual DevBrains earnings exceed ₹20 lakhs, you may need to register for GST. Consult your CA or use our partnered accounting service (available in Dashboard → Tax Documents).</p>
<blockquote>Top earners on DevBrains: ₹8,500/month (part-time, 5 sessions/week) to ₹1,50,000/month (full-time, 60+ sessions/week).</blockquote>`,
    readTimeMins: 5,
    publishedAt: "2026-03-06",
    published: true,
  },
];
